import { BIRDS_SALT, CELL, GRID_SIZE, HAWK_SALT } from '../game/constants';
import { dayNumber } from '../game/daily';
import { cx, cy, key } from '../game/geometry';
import { mulberry32, randInt, type Rng } from '../game/rng';
import type { Network, Point, Puzzle } from '../game/types';
import { pointOnWire, spanConductors, towerFacings, type Facing, type Vec2 } from './wires';
import { towerRoles } from '../game/network';
import { SPRITE } from './sprites';
import { el } from './svg';

/*
 * The ambient inhabitants layer (STYLEGUIDE §5 world layer, §8 item 6).
 *
 * Two or three birds settle on live wires once the grid is on, and leave
 * over the board's edge when it goes dark: they are the world answering the
 * fact that the grid is on, so they come and go with it.
 *
 * The hawk is the exception §5 carves out — weather, not a response to
 * load. It circles its seeded peak from the moment the board mounts and
 * keeps circling through a blackout, because a hawk over a ridge has
 * nothing to do with whether anyone below has power, and a ridge with
 * nothing over it reads as a dead world rather than an unpowered one.
 *
 * Nothing here glows; nothing here reads a rule the player has to learn.
 * The bird count is fixed regardless of wire length so the flourish never
 * rewards wire.
 *
 * All state lives inside this module. The board renderer creates one
 * Inhabitants instance per mount and calls update() at the end of each
 * render() — the module diffs against its own memory to avoid restarting
 * SMIL loops that would otherwise reset on every tap.
 */

const BIRD_COUNT_MIN = 2;
const BIRD_COUNT_MAX = 3;
const LAND_MS = 450;
const TRANSIT_MS = 700;
const FLY_AWAY_MS = 620;
const HAWK_PERIOD_MS = 40000;
/** The board's own width in board units — where the edges a bird leaves over are. */
const BOARD = GRID_SIZE * CELL;
/**
 * Rise over run for a flushed bird's climb. A bird breaking from a wire
 * pulls away almost flat and gains height as it goes; anything near
 * vertical reads as a launch rather than as flight.
 */
const SCATTER_CLIMB = 0.5;
// The orbit is a wide, flat ellipse — a hawk drifts on a thermal, it doesn't
// spin. Kept comfortably inside the peak's cell horizontally so the bird
// never wanders across other tiles.
const HAWK_RADIUS_X = 38;
const HAWK_RADIUS_Y = 10;
/**
 * How high above the cell centre the orbit sits.
 *
 * The hawk rides above the ridge rather than on it. The tallest mountain
 * variant caps at -33 in sprite units and is drawn under a 1.4 vertical
 * scale, so 46 above the cell centre; the body has to clear that with the
 * orbit's own rise on top of it. Wingtips crossing the ridge line behind
 * the bird are fine — a body sitting on the peak is what reads as a dart
 * stuck in the mountain.
 */
const HAWK_LIFT = 46 + HAWK_RADIUS_Y + 6;
/** Half the hawk's wingspan, for keeping the silhouette on the board. */
const HAWK_HALF_H = 25;

interface BirdState {
  /** Which live edge the bird is perched on, keyed as "keyA|keyB". */
  edgeKey: string;
  perch: Vec2;
  /** Where along the wire (0..1) — enough to re-derive the perch and
   *  notice the wire moving beneath it. */
  t: number;
  elem: SVGGElement;
  /** True while a flight animation is in progress — don't queue another. */
  transitioning: boolean;
  /** Handle for the setTimeout that lands the bird — cancellable if the
   *  grid goes dark mid-flight so a landing doesn't resurrect it after. */
  landTimer?: ReturnType<typeof setTimeout>;
}

interface HawkState {
  peak: Point;
  elem: SVGGElement;
}

export class Inhabitants {
  private readonly group: SVGGElement;
  private readonly puzzle: Puzzle;
  private readonly birdRng: Rng;
  private readonly birdCount: number;
  private birds: BirdState[] = [];
  private hawk: HawkState | null = null;
  private didFirstEnergize = false;
  /**
   * What the last render showed. A bird decides where to sit when it lands,
   * not only when the board changes, so the landing callback needs the same
   * view of the grid that update() had.
   */
  private lastFrame: {
    liveEdges: Network['edges'];
    roles: ReturnType<typeof towerRoles>;
    facings: Facing[];
    reduceMotion: boolean;
  } | null = null;

  constructor(group: SVGGElement, puzzle: Puzzle) {
    this.group = group;
    this.puzzle = puzzle;
    const seed = dayNumber();
    // seeded per-day: same flock size and (given the same live edges) same
    // starting perches for every player on the same grid
    this.birdRng = mulberry32(seed ^ BIRDS_SALT);
    this.birdCount = BIRD_COUNT_MIN + randInt(this.birdRng, BIRD_COUNT_MAX - BIRD_COUNT_MIN + 1);
  }

  /**
   * Called at the end of every render. `energized` is true when any tower
   * beyond the plant is reached — that's when the world has power.
   */
  update(net: Network, energized: boolean, reduceMotion: boolean): void {
    // Weather first, and unconditionally: the hawk is up on the empty
    // board before a single tower is placed, and stays up through a
    // blackout (§5).
    this.seedHawk(reduceMotion);

    if (!energized) {
      this.clearBirdsVisibly(reduceMotion);
      this.didFirstEnergize = false;
      return;
    }

    const roles = towerRoles(this.puzzle, net);
    const facings = towerFacings(net);
    const liveEdges = net.edges.filter((e) => e.live);
    if (liveEdges.length === 0) {
      this.clearBirdsVisibly(reduceMotion);
      return;
    }

    if (!this.didFirstEnergize) {
      // First energize this session: seed the flock onto live wires.
      this.didFirstEnergize = true;
      this.seedBirds(liveEdges, roles, facings, reduceMotion);
      return;
    }

    this.lastFrame = { liveEdges, roles, facings, reduceMotion };
    this.restless();
  }

  /**
   * Move every bird that should not be where it is.
   *
   * Three reasons to fly: the wire underneath is no longer live; it is live
   * but has shifted beneath the bird (a new tower can re-anchor a span when
   * a role changes or its terminals re-pair); or it runs up the board while
   * some span runs across it, because a bird stands across a wire and the
   * flock seeds the moment the first tower past the plant is reached, when a
   * growing line is often still all north-south. That last one settles: once
   * a bird is on a level span none of the three hold, so it is one hop
   * rather than a flutter.
   *
   * Run from update() and again from each landing, because a bird that was
   * mid-flight when the board changed has to decide once it is down. Same
   * seeded RNG stream throughout, so the same rearrange yields the same
   * re-landings for every player on the grid.
   */
  private restless(): void {
    const frame = this.lastFrame;
    if (!frame) return;
    const { liveEdges, roles, facings, reduceMotion } = frame;
    if (liveEdges.length === 0) return;
    const hasLevel = liveEdges.some(isLevel);
    const liveByKey = new Map(liveEdges.map((e) => [edgeKey(e.a, e.b), e]));
    for (const bird of this.birds) {
      if (bird.transitioning) continue;
      const edge = liveByKey.get(bird.edgeKey);
      if (edge && (isLevel(edge) || !hasLevel)) {
        const now = this.perchAt(edge, roles, facings, bird.t);
        if (Math.hypot(now.x - bird.perch.x, now.y - bird.perch.y) < 0.5) continue;
      }
      this.flyTo(bird, this.pickPerch(liveEdges, roles, facings), reduceMotion);
    }
  }

  private seedBirds(
    liveEdges: Network['edges'],
    roles: ReturnType<typeof towerRoles>,
    facings: Facing[],
    reduceMotion: boolean,
  ): void {
    this.clearBirds();
    // Prefer distinct wires so the flock spreads across the grid rather than
    // clumping on one edge. If there are fewer live edges than birds, the
    // extras fall back to any edge.
    const taken = new Set<string>();
    for (let i = 0; i < this.birdCount; i++) {
      let spot = this.pickPerch(liveEdges, roles, facings);
      for (let attempt = 0; attempt < 6 && taken.has(spot.edgeKey); attempt++) {
        spot = this.pickPerch(liveEdges, roles, facings);
      }
      taken.add(spot.edgeKey);
      const g = el('g', { class: 'inhabitant bird' }, this.group);
      const u = el('use', { href: SPRITE.bird, x: spot.perch.x, y: spot.perch.y }, g);
      if (!reduceMotion) {
        // A short landing tween: appear a cell above the perch and settle
        // down. Nothing on the surge timeline — this is after-the-fact
        // ambient life, not part of the electrical event.
        el(
          'animateTransform',
          {
            attributeName: 'transform',
            type: 'translate',
            values: `0 -${40}; 0 0`,
            dur: `${LAND_MS}ms`,
            fill: 'freeze',
            begin: `${300 + i * 120}ms`,
          },
          g,
        );
        el(
          'animate',
          {
            attributeName: 'opacity',
            values: '0;1',
            dur: `${LAND_MS}ms`,
            fill: 'freeze',
            begin: `${300 + i * 120}ms`,
          },
          g,
        );
      }
      // held to guard against the SMIL restarting on element re-parent;
      // the use path is written directly in inline coordinates
      void u;
      this.birds.push({
        edgeKey: spot.edgeKey,
        perch: spot.perch,
        t: spot.t,
        elem: g,
        transitioning: false,
      });
    }
  }

  /**
   * Fly a bird from its current perch to a new one along an arced path. The
   * bird lifts off on the winged sprite, follows a quadratic-Bezier arc
   * through a peak above both perches (higher for longer distances), and
   * settles on the destination wire. When the flight ends the flying
   * element is swapped for a fresh perched one — the cleanest way to reset
   * SMIL state ready for the next flight.
   */
  private flyTo(bird: BirdState, dest: PerchPick, reduceMotion: boolean): void {
    if (reduceMotion) {
      // Instant re-perch — bird just pops to the new wire.
      const u = bird.elem.querySelector('use');
      if (u) {
        u.setAttribute('x', String(dest.perch.x));
        u.setAttribute('y', String(dest.perch.y));
      }
      bird.edgeKey = dest.edgeKey;
      bird.perch = dest.perch;
      bird.t = dest.t;
      return;
    }

    const from = bird.perch;
    const to = dest.perch;

    // Update bookkeeping immediately: the next render tick sees the bird
    // as already belonging to the destination edge, so it won't try to
    // schedule another flight for the same displacement.
    bird.edgeKey = dest.edgeKey;
    bird.perch = dest.perch;
    bird.t = dest.t;
    bird.transitioning = true;

    // Arc height rises with the distance so a long flight visibly clears
    // the towers between the two perches instead of skimming them flat.
    const distance = Math.hypot(to.x - from.x, to.y - from.y);
    const arcHeight = 40 + distance * 0.18;
    const ctrl = { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - arcHeight };

    // The perched drawing does not fly: swap it for the winged one and let
    // the flight turn that into the tangent.
    bird.elem.remove();
    const flight = sampleFlight(from, ctrl, to, 14);
    const elem = flyingBird(this.group, flight.mirrored);
    bird.elem = elem;
    kick(beginFlight(elem, flight, TRANSIT_MS));

    bird.landTimer = setTimeout(() => {
      const fresh = el('g', { class: 'inhabitant bird' }, this.group);
      el('use', { href: SPRITE.bird, x: to.x, y: to.y }, fresh);
      elem.remove();
      bird.elem = fresh;
      bird.transitioning = false;
      bird.landTimer = undefined;
      this.restless();
    }, TRANSIT_MS + 30);
  }

  /**
   * The grid went dark — the flock breaks off its wires and leaves the
   * board instead of vanishing on the spot. The direction is the board's,
   * not the array's: each bird breaks for whichever side edge it is nearer
   * and goes out over it, climbing at a shallow angle the whole way. Any
   * bird still mid-flight from a previous scatter is cancelled and replaced
   * with a fresh element at its known perch so we can start cleanly.
   */
  private flyAwayAllBirds(): void {
    const birds = this.birds;
    this.birds = [];
    for (const bird of birds) {
      // Cancel the landing that would otherwise resurrect the bird after
      // its transit finishes, and get rid of the (possibly mid-flight) old
      // element so the fresh SMIL below isn't stacked on top of another.
      if (bird.landTimer) {
        clearTimeout(bird.landTimer);
        bird.landTimer = undefined;
      }
      bird.elem.remove();

      const from = bird.perch;
      // A little spread, taken from where the bird was sitting rather than
      // from a random draw: two birds flushed off the same side still leave
      // on their own lines, and every player on the grid sees the same
      // departure. Reading the seeded stream here would desync the perches
      // the next energize deals.
      const spread = (from.x * 0.137 + from.y * 0.071) % 1;
      const west = from.x < BOARD / 2;
      const destX = west ? -70 - spread * 60 : BOARD + 70 + spread * 60;
      const run = Math.abs(destX - from.x);
      const destY = from.y - run * (SCATTER_CLIMB + spread * 0.25);
      // Bowed just above the straight line: the bird pushes off, then
      // settles onto its climb.
      const ctrl = { x: (from.x + destX) / 2, y: (from.y + destY) / 2 - run * 0.12 };
      // Longer runs get longer flights so the flock leaves at one speed
      // rather than the near birds crawling while the far ones bolt.
      const dur = Math.round(FLY_AWAY_MS * (0.7 + (0.6 * run) / BOARD));

      const flight = sampleFlight(from, ctrl, { x: destX, y: destY }, 12);
      const elem = flyingBird(this.group, flight.mirrored);
      const anims = beginFlight(elem, flight, dur);
      anims.push(
        el(
          'animate',
          {
            attributeName: 'opacity',
            values: '1;1;0',
            keyTimes: '0;0.55;1',
            dur: `${dur}ms`,
            fill: 'freeze',
            begin: 'indefinite',
          },
          elem,
        ),
      );
      kick(anims);
      setTimeout(() => elem.remove(), dur + 40);
    }
  }

  private pickPerch(
    liveEdges: Network['edges'],
    roles: ReturnType<typeof towerRoles>,
    facings: Facing[],
  ): PerchPick {
    // Prefer the spans a bird can actually stand across; fall back to any
    // live edge on a grid that runs entirely north-south.
    const level = liveEdges.filter(isLevel);
    const pool = level.length ? level : liveEdges;
    const e = pool[randInt(this.birdRng, pool.length)];
    // t between 0.3 and 0.7 so the bird sits away from the towers
    const t = 0.3 + this.birdRng() * 0.4;
    return {
      edgeKey: edgeKey(e.a, e.b),
      t,
      perch: this.perchAt(e, roles, facings, t),
    };
  }

  /** Where a perch sits right now — re-derivable, so a moved wire is noticed. */
  private perchAt(
    e: Network['edges'][number],
    roles: ReturnType<typeof towerRoles>,
    facings: Facing[],
    t: number,
  ): Vec2 {
    // Always the core conductor — index 1, the one the spark rides. Since
    // the outer phases dropped to whisper hairlines (STYLEGUIDE §5), a bird
    // on an outer would sit on a thread the eye can barely find.
    const [a, b] = spanConductors(
      e.a,
      roles[e.ai],
      facings[e.ai],
      e.b,
      roles[e.bi],
      facings[e.bi],
    )[1];
    const p = pointOnWire(a, b, t, e.len);
    // Bird sprite has its origin at its feet, so it stands cleanly on the
    // wire when the perch matches the wire's y — a small upward nudge just
    // lifts the tail so it doesn't hang through the amber line.
    return { x: p.x, y: p.y - 1.5 };
  }

  /** Seeded once per mount, and never cleared — the hawk outlasts the grid. */
  private seedHawk(reduceMotion: boolean): void {
    if (this.hawk) return;
    const peaks: Point[] = [];
    for (const [k, t] of Object.entries(this.puzzle.obstacles)) {
      if (t !== 'mtn') continue;
      const p = parseCoord(k);
      if (p) peaks.push(p);
    }
    if (peaks.length === 0) return;
    // The board has no sky above row 0 — the viewBox starts at y = 0 — so a
    // peak up there has nowhere to put a hawk that is not either inside the
    // mountain or cut off at the frame. Pass those over while any other peak
    // is available, and only fall back to one when the day's whole range sits
    // on the top row.
    const roomy = peaks.filter((p) => cy(p) - HAWK_LIFT - HAWK_HALF_H - HAWK_RADIUS_Y >= 0);
    const pool = roomy.length ? roomy : peaks;
    const hawkRng = mulberry32(dayNumber() ^ HAWK_SALT);
    const peak = pool[randInt(hawkRng, pool.length)];
    const centerX = cx(peak);
    const centerY = Math.max(HAWK_HALF_H + HAWK_RADIUS_Y, cy(peak) - HAWK_LIFT);
    const g = el('g', { class: 'inhabitant hawk' }, this.group);
    el('use', { href: SPRITE.hawk, x: centerX, y: centerY }, g);
    if (!reduceMotion) {
      // Slow drift on a flat ellipse; rotate:auto turns the bird into the
      // tangent so the beak leads and the wings sweep the outside of the
      // curve, the way a real thermal-riding hawk banks around a peak.
      const path = describeEllipse(centerX, centerY, HAWK_RADIUS_X, HAWK_RADIUS_Y);
      el(
        'animateMotion',
        {
          dur: `${HAWK_PERIOD_MS}ms`,
          repeatCount: 'indefinite',
          path,
          rotate: 'auto',
        },
        g,
      );
      // A very slight opacity ripple as it curves through — reads as depth
      // without actually calculating any.
      el(
        'animate',
        {
          attributeName: 'opacity',
          values: '0.75;1;0.75',
          dur: `${HAWK_PERIOD_MS}ms`,
          repeatCount: 'indefinite',
        },
        g,
      );
      // Move the anchor to (0,0) so the animateMotion path is absolute.
      const useEl = g.querySelector('use');
      if (useEl) {
        useEl.setAttribute('x', '0');
        useEl.setAttribute('y', '0');
      }
    } else {
      // Reduced motion freezes the orbit, and the frozen pose is the one the
      // press stills and every reduced-motion player get. Square-on the bird
      // is a cross — body along one axis, wings along the other — because
      // the drawing counts on `rotate: auto` to bank it. So park it off the
      // top of the ellipse and bank it there by hand, about its own point.
      const restX = centerX - HAWK_RADIUS_X * 0.55;
      const restY = centerY - HAWK_RADIUS_Y * 0.7;
      const useEl = g.querySelector('use');
      useEl?.setAttribute('x', String(restX));
      useEl?.setAttribute('y', String(restY));
      g.setAttribute('transform', `rotate(-20 ${restX} ${restY})`);
    }
    this.hawk = { peak, elem: g };
  }

  private clearBirds(): void {
    for (const b of this.birds) {
      if (b.landTimer) clearTimeout(b.landTimer);
      b.elem.remove();
    }
    this.birds = [];
  }

  /**
   * Retire this instance: every pending landing is cancelled and everything
   * it drew is removed.
   *
   * A motion-setting change rebuilds the whole SMIL layer around the same
   * group element (`refreshMotion` in board.ts), so an instance that is
   * being replaced has to let go of its timers first — one left running
   * lands a bird into the group the new instance now owns.
   */
  dispose(): void {
    this.clearBirds();
    this.lastFrame = null;
    this.hawk?.elem.remove();
    this.hawk = null;
  }

  /**
   * The grid went dark, so the birds go — visibly, off the side of the
   * board, unless motion is reduced and they simply stop being there. The
   * hawk is untouched either way: it was never answering the grid.
   */
  private clearBirdsVisibly(reduceMotion: boolean): void {
    if (reduceMotion) this.clearBirds();
    else this.flyAwayAllBirds();
  }
}

interface PerchPick {
  edgeKey: string;
  perch: Vec2;
  /** Position along the wire, 0..1. */
  t: number;
}

/**
 * Does this span run more across the board than up it?
 *
 * A bird stands across a wire, not along one, and the sprite is drawn level:
 * on a span that runs up the board the conductor passes straight through the
 * silhouette.
 */
function isLevel(e: Network['edges'][number]): boolean {
  return Math.abs(e.a.c - e.b.c) >= Math.abs(e.a.r - e.b.r);
}

function edgeKey(a: Point, b: Point): string {
  const ka = key(a);
  const kb = key(b);
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

function parseCoord(k: string): Point | null {
  const [r, c] = k.split(',').map(Number);
  if (
    Number.isInteger(r) &&
    Number.isInteger(c) &&
    r >= 0 &&
    c >= 0 &&
    r < GRID_SIZE &&
    c < GRID_SIZE
  ) {
    return { r, c };
  }
  return null;
}

/** The keyframes one flight needs: where the bird is, and where it points. */
interface Flight {
  /** `animateTransform type="translate"` values along the curve. */
  translate: string;
  /** `animateTransform type="rotate"` values turning the bird into the tangent. */
  rotate: string;
  /** True when the flight heads west and the sprite has to be flipped. */
  mirrored: boolean;
}

/**
 * Sample a quadratic Bezier into a flight.
 *
 * The hawk gets this for free — it rides an `animateMotion` and `rotate:
 * auto` turns it into the tangent. These flights are built out of
 * `animateTransform` with an explicit `begin` instead, because dynamically
 * inserted `animateMotion` elements don't start reliably in every browser
 * we support, so the tangent has to be spelled out as a second, additive
 * rotate track alongside the translate.
 *
 * A bird flying west is mirrored, not turned upside down: the sprite is
 * drawn facing +X, so westward flights flip it horizontally and take 180°
 * off the tangent, which leaves the beak leading and the sky still up.
 */
function sampleFlight(from: Vec2, ctrl: Vec2, to: Vec2, steps: number): Flight {
  const pts: Vec2[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const one = 1 - t;
    pts.push({
      x: one * one * from.x + 2 * one * t * ctrl.x + t * t * to.x,
      y: one * one * from.y + 2 * one * t * ctrl.y + t * t * to.y,
    });
  }
  const mirrored = to.x < from.x;
  const angles: number[] = [];
  let prev = 0;
  for (let i = 0; i <= steps; i++) {
    const ahead = pts[Math.min(i + 1, steps)];
    const behind = pts[Math.max(i - 1, 0)];
    let deg = (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI;
    if (mirrored) deg -= 180;
    // Unwrap against the previous keyframe so a tween across the ±180
    // seam turns the short way instead of spinning the bird right round.
    if (i > 0) deg += 360 * Math.round((prev - deg) / 360);
    angles.push(deg);
    prev = deg;
  }
  return {
    translate: pts.map((p) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(';'),
    rotate: angles.map((d) => `${d.toFixed(1)} 0 0`).join(';'),
    mirrored,
  };
}

/**
 * A bird in flight: the winged sprite, mirrored inside its own group when
 * the flight heads west so the outer rotate still works in tangent angles.
 */
function flyingBird(group: SVGGElement, mirrored: boolean): SVGGElement {
  const g = el('g', { class: 'inhabitant bird' }, group);
  const inner = el('g', mirrored ? { transform: 'scale(-1,1)' } : {}, g);
  el('use', { href: SPRITE.birdFly, x: 0, y: 0 }, inner);
  return g;
}

/**
 * Hang a flight's two transform tracks on a group. The rotate is additive
 * so it composes with the translate rather than replacing it, and it comes
 * second so the bird turns about its own body rather than about the board's
 * origin.
 */
function beginFlight(elem: SVGGElement, flight: Flight, dur: number): SVGAnimationElement[] {
  const common = { dur: `${dur}ms`, fill: 'freeze', begin: 'indefinite' };
  return [
    el(
      'animateTransform',
      { attributeName: 'transform', type: 'translate', values: flight.translate, ...common },
      elem,
    ),
    el(
      'animateTransform',
      {
        attributeName: 'transform',
        type: 'rotate',
        values: flight.rotate,
        additive: 'sum',
        ...common,
      },
      elem,
    ),
  ];
}

/**
 * Start dynamically-inserted SMIL. A plain `begin` is measured against SVG
 * document time, which is always in the past by the time we insert;
 * beginElement() in the next frame is the standard way round it.
 */
function kick(anims: SVGAnimationElement[]): void {
  requestAnimationFrame(() => {
    for (const a of anims) a.beginElement();
  });
}

/** A closed elliptical path for `animateMotion`. */
function describeEllipse(cx0: number, cy0: number, rx: number, ry: number): string {
  return `M ${cx0 - rx} ${cy0} A ${rx} ${ry} 0 1 1 ${cx0 + rx} ${cy0} A ${rx} ${ry} 0 1 1 ${cx0 - rx} ${cy0} Z`;
}
